import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getDeployment, getAttendance, terminateDeployment, updateDeployment, getSalaryTemplates, assignSalaryTemplate, getSalaryRevisions } from '../services/api';
import {
  ArrowLeft, UserCheck, MapPin, Calendar, Phone, Building2,
  CreditCard, ClipboardList, X, AlertTriangle, Pencil, DollarSign, History
} from 'lucide-react';
import toast from 'react-hot-toast';

const statusColor = (s) =>
  s === 'active'     ? 'bg-green-50 text-green-700' :
  s === 'completed'  ? 'bg-gray-50 text-gray-500' :
  s === 'terminated' ? 'bg-red-50 text-red-600' :
  s === 'on_hold'    ? 'bg-amber-50 text-amber-700' :
  'bg-blue-50 text-blue-700';

const attendanceColor = (s) =>
  s === 'present'  ? 'bg-green-50 text-green-700' :
  s === 'absent'   ? 'bg-red-50 text-red-600' :
  s === 'half_day' ? 'bg-amber-50 text-amber-700' :
  s === 'leave'    ? 'bg-purple-50 text-purple-700' :
  'bg-gray-50 text-gray-500';

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between py-2 border-b border-gray-50">
    <span className="text-sm text-gray-400">{label}</span>
    <span className="text-sm font-medium text-gray-800">{value || '—'}</span>
  </div>
);

const TerminateModal = ({ id, onClose }) => {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () => terminateDeployment(id, { termination_reason: reason }),
    onSuccess : () => {
      toast.success('Deployment terminated');
      queryClient.invalidateQueries(['deployment', id]);
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to terminate deployment')
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-500" />
            Terminate Deployment
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Reason for termination..." />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={mutation.isPending}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">
              {mutation.isPending ? 'Terminating...' : 'Terminate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditDeploymentModal = ({ dep, onClose }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    designation  : dep.designation || '',
    department   : dep.department || '',
    location     : dep.location || '',
    shift        : dep.shift || '',
    working_hours: dep.working_hours || '',
    working_days : dep.working_days || '',
    weekly_off   : dep.weekly_off || '',
    pay_rate     : dep.pay_rate || '',
    bill_rate    : dep.bill_rate || ''
  });

  const mutation = useMutation({
    mutationFn: () => updateDeployment(dep.id, {
      ...form,
      working_hours: form.working_hours === '' ? undefined : parseFloat(form.working_hours),
      working_days : form.working_days  === '' ? undefined : parseInt(form.working_days),
      pay_rate     : form.pay_rate      === '' ? undefined : parseFloat(form.pay_rate),
      bill_rate    : form.bill_rate     === '' ? undefined : parseFloat(form.bill_rate)
    }),
    onSuccess: () => {
      toast.success('Deployment updated');
      queryClient.invalidateQueries(['deployment', dep.id]);
      onClose();
    },
    onError: (err) => toast.error(
      err.response?.data?.errors?.[0]?.message ||
      err.response?.data?.message ||
      'Failed to update deployment'
    )
  });

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-screen overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">Edit Deployment</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
              <input name="designation" value={form.designation} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <input name="department" value={form.department} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input name="location" value={form.location} onChange={handle}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Shift</label>
              <select name="shift" value={form.shift} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">Select</option>
                <option value="day">Day</option>
                <option value="night">Night</option>
                <option value="rotational">Rotational</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Weekly Off</label>
              <input name="weekly_off" value={form.weekly_off} onChange={handle}
                placeholder="e.g. Sunday"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Working Days/Month</label>
              <input name="working_days" type="number" value={form.working_days} onChange={handle}
                min="1" max="31"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Working Hours/Day</label>
              <input name="working_hours" type="number" value={form.working_hours} onChange={handle}
                min="1" max="24"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pay Rate (₹)</label>
              <input name="pay_rate" type="number" value={form.pay_rate} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bill Rate (₹)</label>
              <input name="bill_rate" type="number" value={form.bill_rate} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={mutation.isPending}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {mutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AssignTemplateModal = ({ dep, onClose }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    template_id     : dep.salary_template_id || '',
    effective_from  : new Date().toISOString().slice(0, 10),
    new_gross_salary: dep.pay_rate || '',
    reason          : ''
  });

  const { data: templates = [] } = useQuery({
    queryKey: ['salary-templates'],
    queryFn : () => getSalaryTemplates().then(r => r.data.data)
  });

  const mutation = useMutation({
    mutationFn: () => assignSalaryTemplate(dep.id, { ...form, new_gross_salary: form.new_gross_salary || undefined }),
    onSuccess : () => {
      toast.success('Salary template assigned');
      queryClient.invalidateQueries(['deployment', dep.id]);
      queryClient.invalidateQueries(['salary-revisions', dep.id]);
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to assign template')
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-gray-900">Assign Salary Template</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Template *</label>
            <select value={form.template_id} onChange={e => setForm({ ...form, template_id: e.target.value })} required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Select template</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Effective From *</label>
            <input type="date" value={form.effective_from} onChange={e => setForm({ ...form, effective_from: e.target.value })} required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Gross Salary (₹)</label>
            <input type="number" value={form.new_gross_salary} onChange={e => setForm({ ...form, new_gross_salary: e.target.value })}
              placeholder="Leave blank to keep current pay rate"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
            <input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })}
              placeholder="e.g. Annual revision, promotion"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={mutation.isPending}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {mutation.isPending ? 'Saving...' : 'Assign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DeploymentDetail = () => {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const [tab, setTab] = useState('overview');
  const [showTerminate, setShowTerminate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showAssignTemplate, setShowAssignTemplate] = useState(false);

  const { data: dep, isLoading } = useQuery({
    queryKey: ['deployment', id],
    queryFn : () => getDeployment(id).then(r => r.data.data)
  });

  const { data: attendance } = useQuery({
    queryKey: ['deployment-attendance', id],
    queryFn : () => getAttendance(id, { limit: 30 }).then(r => r.data.data.records),
    enabled : tab === 'attendance'
  });

  const { data: revisions = [] } = useQuery({
    queryKey: ['salary-revisions', id],
    queryFn : () => getSalaryRevisions(id).then(r => r.data.data),
    enabled : tab === 'salary'
  });

  if (isLoading) return (
    <div className="flex items-center justify-center h-64 text-gray-400">
      Loading deployment...
    </div>
  );

  if (!dep) return (
    <div className="text-center py-12 text-gray-400">Deployment not found</div>
  );

  const tabs = [
    { id: 'overview',   label: 'Overview' },
    { id: 'attendance', label: 'Attendance' },
    { id: 'timesheets', label: 'Timesheets' },
    { id: 'salary',     label: 'Salary' }
  ];

  return (
    <div className="space-y-5">
      {/* Back Button */}
      <button onClick={() => navigate('/deployments')}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft size={16} />
        Back to Deployments
      </button>

      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-start gap-5">
          <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center flex-shrink-0">
            <span className="text-green-600 text-2xl font-bold">
              {dep.candidates?.first_name?.charAt(0)}
            </span>
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl font-semibold text-gray-900">
                {dep.candidates?.first_name} {dep.candidates?.last_name}
              </h1>
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColor(dep.status)}`}>
                {dep.status}
              </span>
            </div>

            <p className="text-sm text-gray-400 mt-1">
              {dep.deployment_no} · {dep.designation || 'No designation'} at {dep.clients?.company_name}
            </p>

            <div className="flex flex-wrap gap-4 mt-3">
              {dep.location && (
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <MapPin size={14} className="text-gray-400" />
                  {dep.location}
                </div>
              )}
              {dep.candidates?.phone && (
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <Phone size={14} className="text-gray-400" />
                  {dep.candidates.phone}
                </div>
              )}
              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                <Calendar size={14} className="text-gray-400" />
                {dep.start_date?.slice(0, 10)} → {dep.end_date?.slice(0, 10) || 'Ongoing'}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {dep.status === 'active' && (
              <button onClick={() => setShowEdit(true)}
                className="flex items-center gap-2 bg-gray-50 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-100">
                <Pencil size={14} />
                Edit
              </button>
            )}
            {dep.status === 'active' && (
              <button onClick={() => setShowTerminate(true)}
                className="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-100">
                Terminate
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {tabs.map(t => (
          <button key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition
              ${tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {tab === 'overview' && (
        <div className="grid grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <UserCheck size={16} className="text-green-600" />
              Assignment Details
            </h3>
            <InfoRow label="Designation"     value={dep.designation} />
            <InfoRow label="Department"      value={dep.department} />
            <InfoRow label="Location"        value={dep.location} />
            <InfoRow label="Employment Type" value={dep.employment_type?.replace('_', ' ')} />
            <InfoRow label="Shift"           value={dep.shift} />
            <InfoRow label="Working Hours"   value={dep.working_hours ? `${dep.working_hours} hrs/day` : null} />
            <InfoRow label="Working Days"    value={dep.working_days ? `${dep.working_days}/month` : null} />
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <CreditCard size={16} className="text-blue-600" />
              Rates & Tenure
            </h3>
            <InfoRow label="Pay Rate"   value={`₹${Number(dep.pay_rate).toLocaleString('en-IN')}/${dep.rate_unit}`} />
            <InfoRow label="Bill Rate"  value={`₹${Number(dep.bill_rate).toLocaleString('en-IN')}/${dep.rate_unit}`} />
            <InfoRow label="Start Date" value={dep.start_date?.slice(0, 10)} />
            <InfoRow label="End Date"   value={dep.end_date?.slice(0, 10)} />
            <InfoRow label="Actual End Date" value={dep.actual_end_date?.slice(0, 10)} />
            {dep.status === 'terminated' && (
              <InfoRow label="Termination Reason" value={dep.termination_reason} />
            )}
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Building2 size={16} className="text-purple-600" />
              Client Contact
            </h3>
            {dep.clients?.client_contacts?.length === 0 ? (
              <div className="text-center py-6 text-gray-400 text-sm">No primary contact set</div>
            ) : (
              dep.clients?.client_contacts?.map(c => (
                <React.Fragment key={c.id}>
                  <InfoRow label="Name"  value={c.name} />
                  <InfoRow label="Phone" value={c.phone} />
                  <InfoRow label="Email" value={c.email} />
                </React.Fragment>
              ))
            )}
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <CreditCard size={16} className="text-amber-600" />
              Bank Details
            </h3>
            {dep.candidates?.candidate_bank_details?.length === 0 ? (
              <div className="text-center py-6 text-gray-400 text-sm">No bank details on file</div>
            ) : (
              dep.candidates?.candidate_bank_details?.map(b => (
                <React.Fragment key={b.id}>
                  <InfoRow label="Bank"           value={b.bank_name} />
                  <InfoRow label="Account Number" value={b.account_number ? `XXXX${b.account_number.slice(-4)}` : null} />
                  <InfoRow label="IFSC"            value={b.ifsc_code} />
                </React.Fragment>
              ))
            )}
          </div>
        </div>
      )}

      {/* ── ATTENDANCE TAB ── */}
      {tab === 'attendance' && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <ClipboardList size={16} className="text-blue-600" />
            Recent Attendance
          </h3>
          {!attendance || attendance.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">No attendance records yet</div>
          ) : (
            <div className="space-y-2">
              {attendance.map(a => (
                <div key={a.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Calendar size={13} className="text-gray-400" />
                    {a.attendance_date?.slice(0, 10)}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-400">
                    {a.working_hours && <span>{a.working_hours} hrs</span>}
                    {a.overtime_hours > 0 && <span>+{a.overtime_hours} OT</span>}
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${attendanceColor(a.status)}`}>
                    {a.status?.replace('_', ' ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── TIMESHEETS TAB ── */}
      {tab === 'timesheets' && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <ClipboardList size={16} className="text-purple-600" />
            Recent Timesheets
          </h3>
          {dep.timesheets?.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">No timesheets submitted yet</div>
          ) : (
            <div className="space-y-3">
              {dep.timesheets?.map(ts => (
                <div key={ts.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{ts.month}/{ts.year}</p>
                    <p className="text-xs text-gray-400">
                      Present: {ts.present_days} · Absent: {ts.absent_days} · Leave: {ts.leave_days}
                    </p>
                  </div>
                  <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-gray-100 text-gray-600 capitalize">
                    {ts.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── SALARY TAB ── */}
      {tab === 'salary' && (
        <div className="space-y-4">
          {/* Current Template Card */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <DollarSign size={16} className="text-blue-600" />
                Current Salary Template
              </h3>
              {dep.status === 'active' && (
                <button onClick={() => setShowAssignTemplate(true)}
                  className="flex items-center gap-1.5 bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-blue-100">
                  <Pencil size={12} />
                  {dep.salary_templates ? 'Change Template' : 'Assign Template'}
                </button>
              )}
            </div>
            {dep.salary_templates ? (
              <div className="bg-blue-50 rounded-xl p-4">
                <p className="text-sm font-semibold text-gray-900">{dep.salary_templates.name}</p>
                {dep.salary_templates.description && (
                  <p className="text-xs text-gray-500 mt-0.5">{dep.salary_templates.description}</p>
                )}
                <p className="text-xs text-gray-400 mt-2">
                  Pay Rate: <span className="font-medium text-gray-700">₹{Number(dep.pay_rate).toLocaleString('en-IN')}/{dep.rate_unit}</span>
                </p>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400 text-sm">
                No salary template assigned yet.{dep.status === 'active' ? ' Click "Assign Template" to set one.' : ''}
              </div>
            )}
          </div>

          {/* Revision History */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-4">
              <History size={16} className="text-purple-600" />
              Salary Revision History
            </h3>
            {revisions.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">No revisions recorded yet</div>
            ) : (
              <div className="relative">
                <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-100" />
                <div className="space-y-4">
                  {revisions.map((rev, i) => (
                    <div key={rev.id} className="flex gap-4 relative">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 z-10
                        ${i === 0 ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                        <DollarSign size={12} />
                      </div>
                      <div className="flex-1 pb-1">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900">
                            {rev.new_template?.name || 'Template assigned'}
                          </p>
                          <span className="text-xs text-gray-400">
                            {rev.effective_from?.slice(0, 10)}
                          </span>
                        </div>
                        {rev.old_template && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            Changed from: {rev.old_template.name}
                          </p>
                        )}
                        {(rev.old_gross_salary || rev.new_gross_salary) && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            Pay: {rev.old_gross_salary ? `₹${Number(rev.old_gross_salary).toLocaleString('en-IN')}` : '—'}
                            {' → '}
                            {rev.new_gross_salary ? `₹${Number(rev.new_gross_salary).toLocaleString('en-IN')}` : '—'}
                          </p>
                        )}
                        {rev.reason && (
                          <p className="text-xs text-gray-400 mt-0.5 italic">"{rev.reason}"</p>
                        )}
                        <p className="text-xs text-gray-300 mt-1">by {rev.users?.name || 'System'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showTerminate && <TerminateModal id={id} onClose={() => setShowTerminate(false)} />}
      {showEdit && <EditDeploymentModal dep={dep} onClose={() => setShowEdit(false)} />}
      {showAssignTemplate && <AssignTemplateModal dep={dep} onClose={() => setShowAssignTemplate(false)} />}
    </div>
  );
};

export default DeploymentDetail;
